import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk'
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react'
import { config } from '../../../../config'
import { featureFlagNameToValueMap, useAccount, useAuth } from '../../../../hooks'
import { logFactory } from '../../../../util'
import { Role } from '../../../../constants/role'

type FeatureFlagKey = keyof typeof featureFlagNameToValueMap

interface IdentifyCustomProps {
  email?: string
  impersonatorEmail?: string
}

type CustomValue = string | number | boolean | (string | number | boolean)[]

interface Identity {
  key: string
  custom: IdentifyCustomProps
}

const { LAUNCH_DARKLY_CLIENT_ID } = config
const log = logFactory('FeatureFlagProvider')

/**
 * Whether LaunchDarkly has finished identifying the user. Until it has, every flag reads as its
 * falsy default, which is indistinguishable from "the flag is off" - so a feature that must not act
 * before its flag is known (a scheduling guardrail, say) has to wait for this rather than trust the
 * value it gets. Children still render throughout; this only reports readiness.
 */
const FeatureFlagsReadyContext = createContext(false)

const useAreFeatureFlagsReady = (): boolean => useContext(FeatureFlagsReadyContext)

const FeatureFlagProvider: FC = ({ children }) => {
  const { user } = useAuth()
  const { account: myAccount } = useAccount(user?.account_id as string)
  const [isLoadingFlags, beLoadingFlags] = useState(true)
  const launchDarklyClient = useLDClient()
  const accountId = user?.account_id

  const isFitter = Boolean(
    user?.roles?.includes(Role.ADMIN_LIGHT) ||
      user?.roles?.includes(Role.EMPLOYEE_FITTER) ||
      user?.roles?.includes(Role.EXTERNAL_TA_FITTER) ||
      user?.roles?.includes(Role.EXTERNAL_NETWORK_FITTER) ||
      user?.roles?.includes(Role.INTERNAL_FITTER) ||
      user?.roles?.includes(Role.INTERNAL_TA_FITTER) ||
      user?.roles?.includes(Role.PARTNER_FITTER) ||
      user?.roles?.includes(Role.READ_ONLY) ||
      user?.roles?.includes(Role.SALES) ||
      user?.roles?.includes(Role.SUPPORT)
  )

  const custom = useMemo<Identity['custom']>(
    () => ({
      country: myAccount?.country,
      email: myAccount?.email,
      fitter_country: myAccount?.country,
      custom: {
        isFitter,
      },
      impersonatorEmail: user?.email ?? myAccount?.email,
    }),
    [myAccount, user]
  )

  useEffect(() => {
    if (!accountId || !launchDarklyClient) {
      log('⏳ Waiting for feature flag dependencies to load...')

      return
    }

    const identify = async () => {
      const identity = {
        custom: custom as Record<string, CustomValue>,
        key: accountId,
      }

      log('🚩 Launch Darkly identify', identity)

      try {
        const flags = await launchDarklyClient?.identify(identity)

        // Invert the flags to make them easier to read in the logs
        const humanReadableFlags = Object.entries(featureFlagNameToValueMap).reduce((acc, [key, value]) => {
          acc[key as FeatureFlagKey] = Boolean(flags?.[value as string])

          return acc
        }, {} as Record<FeatureFlagKey, boolean>)

        log('🚩 Launch Darkly flags', humanReadableFlags)
      } catch (error) {
        log('❌ Launch Darkly identify failed - proceeding with default flag values', error)
      } finally {
        // Always settle, including on failure: consumers that wait for readiness would otherwise
        // block forever on an LD outage. Defaults are the falsy ones, so failing to identify leaves
        // every feature off rather than on.
        beLoadingFlags(false)
      }
    }

    identify()
  }, [accountId, custom, isLoadingFlags, launchDarklyClient])

  return <FeatureFlagsReadyContext.Provider value={!isLoadingFlags}>{children}</FeatureFlagsReadyContext.Provider>
}

const LaunchDarklyProvider = withLDProvider({
  clientSideID: LAUNCH_DARKLY_CLIENT_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(FeatureFlagProvider)

export { LaunchDarklyProvider as FeatureFlagProvider, useAreFeatureFlagsReady }
